import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

/**
 * SpinnerService — reference-counted loading indicator with delayed visibility.
 *
 * The HTTP loading interceptor calls `show()` / `hide()` around each request.
 * Visibility is delayed so fast calls (when response delay is disabled) do not flash.
 */
@Injectable({ providedIn: 'root' })
export class SpinnerService {
  /** Delay before the overlay appears (aligns with UX: show loading for ops > ~300ms). */
  static readonly SHOW_DELAY_MS = 300;
  /** Minimum time the overlay stays visible once shown. */
  static readonly MIN_VISIBLE_MS = 250;

  private readonly count$ = new BehaviorSubject<number>(0);
  private readonly visibleSubject = new BehaviorSubject<boolean>(false);

  private showTimer: ReturnType<typeof setTimeout> | null = null;
  private hideTimer: ReturnType<typeof setTimeout> | null = null;
  private shownAt = 0;

  readonly visible$: Observable<boolean> = this.visibleSubject.pipe(distinctUntilChanged());

  /** True while at least one tracked request is in flight (ignores show delay). */
  get isBusy(): boolean {
    return this.count$.value > 0;
  }

  show(): void {
    const next = this.count$.value + 1;
    this.count$.next(next);
    if (next !== 1) return;

    this.clearHideTimer();
    if (this.visibleSubject.value) return;

    this.clearShowTimer();
    this.showTimer = setTimeout(() => {
      this.showTimer = null;
      if (this.count$.value > 0) {
        this.shownAt = Date.now();
        this.visibleSubject.next(true);
      }
    }, SpinnerService.SHOW_DELAY_MS);
  }

  hide(): void {
    const next = Math.max(0, this.count$.value - 1);
    this.count$.next(next);
    if (next > 0) return;

    this.clearShowTimer();
    if (!this.visibleSubject.value) return;

    const elapsed = Date.now() - this.shownAt;
    const remaining = Math.max(0, SpinnerService.MIN_VISIBLE_MS - elapsed);
    this.clearHideTimer();
    this.hideTimer = setTimeout(() => {
      this.hideTimer = null;
      if (this.count$.value === 0) {
        this.visibleSubject.next(false);
      }
    }, remaining);
  }

  reset(): void {
    this.clearShowTimer();
    this.clearHideTimer();
    this.count$.next(0);
    this.visibleSubject.next(false);
  }

  private clearShowTimer(): void {
    if (this.showTimer == null) return;
    clearTimeout(this.showTimer);
    this.showTimer = null;
  }

  private clearHideTimer(): void {
    if (this.hideTimer == null) return;
    clearTimeout(this.hideTimer);
    this.hideTimer = null;
  }
}
